import React, { useState, useMemo } from "react";
import { Modal, Row, Col, Table, Spinner, Placeholder } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import moment from "moment";
import { X } from "lucide-react";
import "./LandlordDetailsModal.css";

const LandlordDetailsModal = ({
  showModal,
  selectedLandlord,
  landlordMetricsList,
  loadingDetails,
  onHide,
}) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Get unique workspaces for the dropdown
  const uniqueWorkspaces = useMemo(() => {
    if (!landlordMetricsList) return [];
    const workspaces = landlordMetricsList.map(item => item.workspaceId).filter(Boolean);
    const uniqueIds = [...new Set(workspaces.map(ws => ws._id))];
    return uniqueIds.map(id => workspaces.find(ws => ws._id === id));
  }, [landlordMetricsList]);

  // Filtering logic
  const filteredList = useMemo(() => {
    if (!landlordMetricsList) return [];
    return landlordMetricsList.filter((item) => {
      const workspaceMatch = workspaceFilter === "all" || item.workspaceId?._id === workspaceFilter;
      
      const isDeleted = item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;
      const statusMatch = statusFilter === "all" || 
        (statusFilter === "active" && !isDeleted) || 
        (statusFilter === "deleted" && isDeleted);

      const hasCheck = item.tenant_check === true || item.tenant_checks === true || (typeof item.tenant_checks === 'number' && item.tenant_checks > 0);
      const typeMatch = typeFilter === "all" || 
        (typeFilter === "check" && hasCheck) || 
        (typeFilter === "profile" && !hasCheck);

      return workspaceMatch && statusMatch && typeMatch;
    });
  }, [landlordMetricsList, workspaceFilter, statusFilter, typeFilter]);

  // Calculate metrics for summary cards
  const summaryData = useMemo(() => {
    if (!selectedLandlord) return { profiles: 0, deleted: 0, checks: 0, workspaces: [] };
    
    // Calculate everything from the filtered list to ensure filters are respected
    let profilesCount = 0;
    let deletedCount = 0;
    let checksCount = 0;
    const workspacesMap = new Map();

    if (landlordMetricsList && landlordMetricsList.length > 0) {
      const tenantIds = new Set();
      filteredList.forEach(item => {
        const isDeleted = item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;
        
        // Track unique tenants
        if (item.tenant_id?._id) {
          tenantIds.add(item.tenant_id._id);
        }
        
        if (isDeleted) {
          deletedCount++;
        }

        // Count checks
        const hasCheck = item.tenant_check === true || item.tenant_checks === true || (typeof item.tenant_checks === 'number' && item.tenant_checks > 0);
        if (hasCheck) {
          checksCount += (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1);
        }

        // Track workspaces
        if (item.workspaceId?._id) {
          workspacesMap.set(item.workspaceId._id, item.workspaceId);
        }
      });
      // profilesCount includes unique non-deleted tenants + all deleted records 
      // (since deleted records don't have IDs to de-duplicate by)
      profilesCount = tenantIds.size;
    } else if (!landlordMetricsList) {
      // Fallback to selectedLandlord if list is not loaded yet (initial state)
      profilesCount = selectedLandlord.tenantCount || 0;
      deletedCount = selectedLandlord.deletedTenantCount || 0;
      checksCount = selectedLandlord.tenantChecks || 0;
      if (selectedLandlord.workspaces) {
        selectedLandlord.workspaces.forEach(ws => workspacesMap.set(ws._id, ws));
      }
    }

    return {
      profiles: profilesCount,
      deleted: deletedCount,
      checks: checksCount,
      workspaces: Array.from(workspacesMap.values())
    };
  }, [selectedLandlord, landlordMetricsList, filteredList]);

  // Calculate unfiltered counts for header card
  const unfilteredCounts = useMemo(() => {
    if (!selectedLandlord) return { profiles: 0, checks: 0, deleted: 0 };

    let profilesCount = 0;
    let deletedCount = 0;
    let checksCount = 0;

    if (landlordMetricsList && landlordMetricsList.length > 0) {
      const tenantIds = new Set();
      landlordMetricsList.forEach(item => {
        const isDeleted = item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;

        if (item.tenant_id?._id) {
          tenantIds.add(item.tenant_id._id);
        }

        if (isDeleted) {
          deletedCount++;
        }

        const hasCheck = item.tenant_check === true || item.tenant_checks === true || (typeof item.tenant_checks === 'number' && item.tenant_checks > 0);
        if (hasCheck) {
          checksCount += (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1);
        }
      });

      profilesCount = tenantIds.size;
    } else if (!landlordMetricsList) {
      profilesCount = selectedLandlord.tenantCount || 0;
      deletedCount = selectedLandlord.deletedTenantCount || 0;
      checksCount = selectedLandlord.tenantChecks || 0;
    }

    return { profiles: profilesCount, checks: checksCount, deleted: deletedCount };
  }, [selectedLandlord, landlordMetricsList]);

  // Find repeated tenants in the filtered list for highlighting
  const repeatedTenantIds = useMemo(() => {
    if (!filteredList) return new Set();
    const tenantCounts = new Map();
    filteredList.forEach(item => {
      if (item.tenant_id?._id) {
        tenantCounts.set(item.tenant_id._id, (tenantCounts.get(item.tenant_id._id) || 0) + 1);
      }
    });
    const repeatedIds = new Set();
    tenantCounts.forEach((count, tenantId) => {
      if (count > 1) {
        repeatedIds.add(tenantId);
      }
    });
    return repeatedIds;
  }, [filteredList]);

  return (
    <Modal 
      show={showModal} 
      onHide={onHide} 
      size="xl" 
      dialogClassName="modal-90w"
      centered 
      scrollable 
      className="landlord-metrics-modal"
    >
      <Modal.Header className="landlord-details-header">
        <div className="d-flex justify-content-between align-items-start w-100">
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h4 className="landlord-details-name">
                  Landlord {selectedLandlord?.landlord?.fname_companyName} {selectedLandlord?.landlord?.lname}
                </h4>
                <p className="landlord-details-email">
                  {selectedLandlord?.landlord?.email || "No email provided"}
                </p>
              </div>
              <button className="modal-close-btn" onClick={onHide}>
                <X size={20} />
              </button>
            </div>
            <div className="landlord-counts-badges mt-3 d-flex gap-3">
              <span className="landlord-count-badge">
                <strong>{unfilteredCounts.profiles}</strong> Profiles
              </span>
              <span className="landlord-count-badge">
                <strong>{unfilteredCounts.checks}</strong> Checks
              </span>
              <span className="landlord-count-badge text-deleted">
                <strong>{unfilteredCounts.deleted}</strong> Deleted
              </span>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 pb-5 pt-0">
        {loadingDetails ? (
          <ModalSkeleton />
        ) : selectedLandlord && (
          <>
            {/* Summary Cards */}
            <Row className="g-4 mb-5">
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Total Profiles</div>
                  <div className="landlord-summary-value">{summaryData.profiles}</div>
                  {statusFilter == "all" && (
                    <div className="landlord-summary-sub text-deleted">{summaryData.deleted} deleted</div>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Total Tenant Checks</div>
                  <div className="landlord-summary-value">{summaryData.checks}</div>
                  <div className="landlord-summary-sub text-muted">across all workspaces</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Workspaces</div>
                  <div className="landlord-summary-value">{summaryData.workspaces.length}</div>
                  <div className="landlord-summary-sub text-muted" title={summaryData.workspaces.map(ws => ws.name).join(', ')}>
                    {summaryData.workspaces.map(ws => ws.name).join(', ')}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Filter Section */}
            <div className="modal-filter-section mb-4">
                <div className="workspace-select-container">
                  <label className="filter-pill-label mb-2 d-block">Workspace</label>
                  <select 
                    className="workspace-select"
                    value={workspaceFilter}
                    onChange={(e) => setWorkspaceFilter(e.target.value)}
                  >
                    <option value="all">All workspaces</option>
                    {uniqueWorkspaces.map(ws => (
                      <option key={ws._id} value={ws._id}>{ws.name}</option>
                    ))}
                  </select>
                </div>

              <div className="filter-pills-row mt-3">
                <div className="filter-pill-group">
                  <span className="filter-pill-label">Status</span>
                  <div className="filter-pill-options">
                    {['all', 'active', 'deleted'].map(s => (
                      <div 
                        key={s}
                        className={`modal-filter-pill ${statusFilter === s ? 'active' : ''}`}
                        onClick={() => setStatusFilter(s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="filter-pill-group">
                  <span className="filter-pill-label">Type</span>
                  <div className="filter-pill-options">
                    {['all', 'profile', 'check'].map(t => (
                      <div 
                        key={t}
                        className={`modal-filter-pill ${typeFilter === t ? 'active' : ''}`}
                        onClick={() => setTypeFilter(t)}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="table-responsive">
              <Table hover className="landlord-modal-table mb-0">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th>TENANT NAME</th>
                    <th>EMAIL</th>
                    <th>WORKSPACE</th>
                    <th>JOINED DATE</th>
                    <th>TYPE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length > 0 ? (
                    filteredList.map((item, index) => {
                      const isDeleted = item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;
                      const isRepeated = item.tenant_id?._id && repeatedTenantIds.has(item.tenant_id._id);
                       return (
                         <tr key={item._id} className={isRepeated ? "tenant-repeated" : ""}>
                          <td className="text-center text-muted small">
                            {index + 1}
                          </td>
                          <td className="fw-700">
                            {item.tenant_id?.fname} {item.tenant_id?.lname}
                          </td>
                          <td className="text-muted small">
                            {item.tenant_id?.email || "N/A"}
                          </td>
                          <td className="fw-600">
                            {item.workspaceId?.name || "N/A"}
                          </td>
                          <td className="text-muted small fw-medium">
                            {item.created_at
                              ? moment(item.created_at).format("DD MMM YYYY, HH:mm")
                              : "N/A"}
                          </td>
                          <td>
                            {(item.tenant_check || item.tenant_checks) ? (
                              <span className="type-badge-check">Check</span>
                            ) : (
                              <span className="type-badge-profile">Profile</span>
                            )}
                          </td>
                          <td>
                            <span className={isDeleted ? "status-badge-deleted" : "status-badge-active"}>
                              {isDeleted ? "Deleted" : "Active"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        No results found matching your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LandlordDetailsModal;